// use client: usePathname drives the Umrah/Business copy variants
'use client'

import type { ReactNode } from 'react'

import '@/styles/bootstrap-icons.css'
import '@/views/digital-banking/digital-banking.css'

import { Col, Container } from 'react-bootstrap'
import parse from 'html-react-parser'
import { usePathname } from 'next/navigation'

import { useContactData } from '@/app/GlobalChromeContext'
import EnquiryForm from '@/components/forms/EnquiryForm'
import { formatTitleWithItalic, parseCmsHtml } from '@/utils'
import { cmsImageSrc } from '@/utils/cmsImageSrc'

interface LetsConnectProps {
  title?: ReactNode
  subtitle?: ReactNode
}

function LetsConnect({ title, subtitle }: LetsConnectProps) {
  // Contact data is seeded at build time by layout.tsx -> AppShell and published
  // on GlobalChromeContext (this component has 54 call sites, so threading a prop
  // through them all would be a large, error-prone diff). A Strapi publish
  // rebuilds and redeploys the site, so there is nothing to revalidate on mount.
  // See tests/no-client-cms-fetch.spec.ts.
  const data = useContactData()
  const pathname = usePathname()
  // Pure functions of `pathname` — derived during render rather than mirrored
  // into state from an effect, which cost two extra render passes per navigation.
  const isUmrah = pathname?.includes('personal-financing-i-umrah') ?? false
  const isBusiness = pathname?.includes('/business/') ?? false
  const contactTitle = !title
    ? 'Have a question about our products or promotions?'
    : typeof title === 'string'
      ? parse(title)
      : title
  const contactSubtitle = !subtitle ? '' : typeof subtitle === 'string' ? parse(subtitle) : subtitle
  const isTitleEmptyString = typeof title === 'string' && title.trim() === ''

  return (
    <Container className="container" id="lets-connect">
      <div className="inward-container">
        {/* Heading for desktop only (visibility controlled via CSS to avoid SSR hydration mismatch) */}
        {!isTitleEmptyString &&
          (isUmrah ? (
            <h2 className="heading mb-2 text-center d-none d-xl-block">Hubungi kami</h2>
          ) : isBusiness ? (
            <h2 className="heading mb-2 text-center d-none d-xl-block">Reach out to us!</h2>
          ) : (
            <h2 className="heading mb-2 text-center d-none d-xl-block">{contactTitle}</h2>
          ))}
        <div data-aos="fade-up" data-aos-delay="150" className="mb-5 d-none d-xl-block">
          {isUmrah ? (
            <p
              style={{
                color: 'var(--alrajhi-black)',
                textAlign: 'center',
                fontSize: isTitleEmptyString ? '24px' : '18px'
              }}
            >
              Sila isikan butiran anda dan wakil perkhidmatan kami akan hubungi anda.
            </p>
          ) : isBusiness ? (
            <p
              style={{
                color: 'var(--alrajhi-black)',
                textAlign: 'center',
                fontSize: isTitleEmptyString ? '24px' : '18px'
              }}
            >
              Share your details, and we'll get back to you as soon as possible.
            </p>
          ) : (
            <p
              style={{
                color: 'var(--alrajhi-black)',
                textAlign: 'center',
                fontSize: isTitleEmptyString ? '24px' : '18px'
              }}
            >
              {contactSubtitle || 'Submit your details and we will be in touch soon.'}
            </p>
          )}
        </div>
        <div
          className="row"
          style={{ justifyContent: 'center' }}
          data-aos="fade-up"
          data-aos-delay="150"
        >
          <div className="col-xl-8">
            <EnquiryForm
              title={
                isUmrah
                  ? 'Hubungi kami'
                  : isBusiness
                    ? 'Reach out to us!'
                    : !isTitleEmptyString
                      ? contactTitle
                      : null
              }
              description={
                isUmrah
                  ? 'Sila isikan butiran anda dan wakil perkhidmatan kami akan hubungi anda.'
                  : isBusiness
                    ? "Share your details, and we'll get back to you as soon as possible."
                    : contactSubtitle || 'Submit your details and we will be in touch soon.'
              }
            />
          </div>
          <div className="col-xl-4 text-center d-flex flex-column gap-4 mt-4 mt-xl-0">
            {data?.map(item => (
              <Col
                key={item?.id}
                className="contact-box d-flex flex-column align-items-center justify-content-center text-center"
                data-aos="fade-up"
                data-aos-delay="150"
              >
                <div className="coming-soon-image-container">
                  <img
                    src={cmsImageSrc(item?.image?.[0]?.url || item?.imageLink || undefined)}
                    alt={`ICON ${item?.name}`}
                    className="d-block"
                    width="50"
                    height="50"
                  />
                </div>

                <h6>{formatTitleWithItalic(item.name ?? '')}</h6>
                {item.description && <div>{parseCmsHtml(item.description)}</div>}
              </Col>
            ))}
          </div>
        </div>
      </div>
    </Container>
  )
}

export default LetsConnect
